import React, { useEffect, useState } from 'react';
import { Typography, Icon, IconButton, ClickAwayListener, PopperPlacementType } from '@material-ui/core';
import Tooltip from '../../atoms/Tooltip';
import LabelWithSymbols from '../../atoms/LabelBySymbols';
import Button from '../../atoms/Button';
import { callCrewNavi } from '../../../external/GinieBrowser/SelectTabPage';

export interface AnnotationTooltipProps {
  content: string;
  url?: string | null;
  label?: string | null;
  placement?: PopperPlacementType;
  closeOnScroll?: boolean;
  customStyle?: React.CSSProperties;
  id?: string;
  activeTooltipId?: string;
  setActiveTooltipId?: (id: string) => void;
}

/**
 * ツールチップコンポーネント
 * @param content 表示する内容
 * @param url PDFのリンク
 * @param label リンクに表示する文字
 * @param placement ツールチップ位置
 * @param closeOnScroll スクロールする際ツールチップを閉じる
 * @param customStyle CSSプロパティなどの設定
 * ここからは同じページで「複数のツールチップ」がある場合のみ設定(マウスオーバー中のツールチップ以外を閉じる)
 * @param id ツールチップID
 * @param activeTooltipId アクティブまたは開いているツールチップ
 * @param setActiveTooltipId アクティブ値を更新
 * @returns
 */

const AnnotationTooltip: React.FC<AnnotationTooltipProps> = ({
  content,
  url,
  label,
  placement = 'top',
  closeOnScroll = true,
  customStyle = {},
  id,
  activeTooltipId,
  setActiveTooltipId,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  /** タッチデバイスかどうか判定 */
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  /** 現在開いているツールチップID */
  const isActive = id && setActiveTooltipId && activeTooltipId === id;

  /** ツールチップを閉じる */
  const handleClose = () => {
    setTimeout(() => (id && setActiveTooltipId ? setActiveTooltipId('') : setIsOpen(false)), 200);
  };

  /** ツールチップを開く */
  const handleOpen = () => {
    setTimeout(() => (id && setActiveTooltipId ? setActiveTooltipId(id) : setIsOpen(true)), 200);
  };

  /** クリックしてツールチップを開く、閉じる */
  const handleClick = () => {
    if (id && setActiveTooltipId) {
      activeTooltipId ? setActiveTooltipId('') : handleOpen();
    } else {
      setIsOpen(!isOpen);
    }
  };

  /** スクロールする際ツールチップを閉じる */
  const handleScroll = () => {
    (isOpen || isActive) && handleClose();
  };

  useEffect(() => {
    if (closeOnScroll) {
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  });

  return (
    <>
      {content && (
        <ClickAwayListener onClickAway={() => handleClose()} touchEvent='onTouchStart' mouseEvent='onMouseDown'>
          <Tooltip
            open={isActive || isOpen}
            arrow
            placement={placement}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            classes={{ popper: 'custom-tooltip' }}
            className='annotation-tooltip'
            data-testid='annotation-tooltip'
            PopperProps={{
              modifiers: {
                preventOverflow: {
                  enabled: true,
                },
                eventListeners: {
                  enabled: true,
                  scroll: false,
                  resize: true,
                },
              },
              onMouseLeave: () => !isTouch && handleClose(),
            }}
            title={
              <Typography variant='body2' component='div' style={{ ...customStyle }}>
                <LabelWithSymbols data-testid='annotation-content' text={content} />
                {url && (
                  <>
                    <div style={{ height: '7px' }} />
                    <Button
                      className='u-mln4'
                      type='transparent'
                      label={<span className='u-text-underline u-text-white'>{label}</span>}
                      rightIcon='launch'
                      iconClassName='u-text-white'
                      data-testid='annotation-openpdf'
                      onClick={() => callCrewNavi(url)}
                    />
                  </>
                )}
              </Typography>
            }>
            <IconButton
              data-testid='tooltip-target'
              onMouseEnter={() => !isTouch && handleOpen()}
              onFocus={handleOpen}
              onBlur={() => handleClose()}
              onClick={handleClick}
              size='small'>
              <Icon className='u-text-secondary u-icon-s u-text-middle u-ml4'>help</Icon>
            </IconButton>
          </Tooltip>
        </ClickAwayListener>
      )}
    </>
  );
};

export default AnnotationTooltip;
